/**
 * Responsibility: say which language the card is printed in, so the
 * search looks in the right half of the catalogue.
 *
 * **This is not the same argument as the game picker, though it looks
 * identical.** Knowing the game buys geometry - where the name and the
 * number are printed. Knowing the language buys nothing geometric at
 * all: a Japanese card has the same artwork, the same collector number
 * and the same layout as its English twin. What it buys is a catalogue
 * that can actually answer, because the name printed on that card is
 * `サンダース` where the catalogue says `Jolteon`, and no amount of
 * fuzzy matching bridges a script.
 *
 * **Saying nothing searches everything, and that is deliberate.** An
 * empty choice must not quietly become an English filter: somebody
 * holding a Japanese card would be told, confidently, that it does not
 * exist. Measured, the corpus is 508 Japanese Pokemon against 242
 * English, so this is the common case rather than an edge one.
 *
 * Only languages the readers have been scored on are offered as
 * filters. An option here is a promise that narrowing to it helps; the
 * rest are served by "Any".
 */
import { ToggleButton, ToggleButtonGroup, Tooltip, Typography, Stack } from '@mui/material';

/** A language the catalogue can be narrowed to, or nothing. */
export type ScanLanguage = 'english' | 'japanese' | '';

const CHOICES: { value: ScanLanguage; label: string; hint: string }[] = [
  { value: 'english', label: 'English', hint: 'Narrows to English printings.' },
  {
    value: 'japanese',
    label: 'Japanese',
    hint: 'Narrows to Japanese printings. The name is read less reliably.',
  },
  { value: '', label: 'Any', hint: 'Searches every language. Slower and less precise.' },
];

export function LanguagePicker({
  language,
  onChange,
  disabled = false,
}: {
  language: ScanLanguage;
  onChange: (next: ScanLanguage) => void;
  disabled?: boolean;
}) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        What language is it printed in?
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={language}
        disabled={disabled}
        // `null` arrives when the pressed button was already selected.
        // Clearing it would silently widen the search back to every
        // language without saying so, so the current choice is kept.
        onChange={(_event, next: ScanLanguage | null) => {
          if (next !== null) onChange(next);
        }}
        aria-label="What language the card is printed in"
      >
        {CHOICES.map((choice) => (
          <Tooltip key={choice.label} title={choice.hint}>
            <ToggleButton value={choice.value} aria-label={choice.label}>
              {choice.label}
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}
